/**
 * Who was on a call, from the list response alone.
 *
 * One function because two things need the same answer and they must not
 * disagree: the row draws it, and the search box matches against it. A row
 * that shows "Mark Magic" and then does not come back when you type "Mark" is
 * the specific bug this shape prevents.
 *
 * `participants_preview` is upstream's — "first four distinct human identities
 * in join order" — and its bound is upstream's to set. It is NOT re-sliced or
 * re-ordered here; `total` is carried alongside so a caller can say "+2"
 * without pretending the preview is the roster.
 */

import type { RoomSummary } from '@era/shared'

export interface RowPerson {
  /** Stable across renders: the identity, or a marker for the local fallback. */
  key: string
  /** Upstream's name, this app's creator name, or the identity — in that order. */
  name: string
}

export interface RowPeople {
  people: RowPerson[]
  /**
   * How many people were on the call, which can exceed `people.length`.
   *
   * `participant_count` is upstream's count of who CONNECTED (or, before
   * upstream has answered, the local count of who a token was minted for), and
   * the preview stops at four. A five-person call must not lose its fifth
   * person to the preview's bound.
   */
  total: number
  /**
   * True when these names came from the control plane rather than from this
   * app's own record of who created the room.
   *
   * False means the listing has not answered about this session yet — normal
   * for the first seconds of a room's life — so the single name is the
   * creator's and the others are only a count.
   */
  named: boolean
}

export function peopleOf(room: RoomSummary): RowPeople {
  const preview = room.participants_preview

  if (preview.length > 0) {
    return {
      people: preview.map((participant) => ({
        key: participant.identity,
        // `name` is nullable upstream: a participant minted through a raw
        // token API need never have had one. The identity is ugly but true,
        // and `Monogram` already knows not to render `u_a` as "U_".
        name: participant.name?.trim() || participant.identity,
      })),
      total: Math.max(room.participant_count, preview.length),
      named: true,
    }
  }

  return {
    people: [{ key: 'creator', name: room.created_by_name }],
    total: Math.max(room.participant_count, 1),
    named: false,
  }
}
